import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpUtilsService } from '../../commons/http-uilis.service';
import { Observable, catchError, map, mergeMap } from 'rxjs';
import { JsonConvert } from 'json2typescript';
import { Paging } from '../../../model/paging/paging';
import { environment } from '../../../environment/environment';
import { keycloakLogout } from '../../../auth/keycloak.config';
import { Channels, ChannelsData, ChannelsDataArray } from '../../../model/channel/channel';

@Injectable({
    providedIn: 'root'
})
export class ChannelService {
    private _url: string = `${environment.baseAPIURL}/settings`;
    constructor(private _http: HttpClient, private _httpUtils: HttpUtilsService) { }

    GETPaginatedChannels(paging:Paging): Observable<Channels>  | any{
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.get(`${this._url}/channels${this.buildQueryString(paging)}`,{ headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            const jsonConvert: JsonConvert = new JsonConvert();
                            const response: ChannelsDataArray = jsonConvert.deserializeObject(res, ChannelsDataArray);
                            if (response.success) {
                            	return response;
                            } else {
                            	this._httpUtils.handleError(response);
                                return;
                            }
                        }, catchError(err => {
                            if (err.status === 401) {
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    GETChannelById(channelId:string): Observable<Channels>  | any{
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.get(`${this._url}/channels/${channelId}`,{ headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            const jsonConvert: JsonConvert = new JsonConvert();
                            const response: ChannelsData = jsonConvert.deserializeObject(res, ChannelsData);
                            if (response.success) {
                            	return response;
                            } else {
                            	this._httpUtils.handleError(response);
                                return;
                            }
                        }, catchError(err => {
                            if (err.status === 401) {
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    DELETEChannel(channelId:string){
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.delete(`${this._url}/channels/${channelId}`, { headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            return res;
                        }, catchError(err => {
                            if (err.status === 401) { // Unauthorized
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }   

    POSTChannel(body:Channels){
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.post(`${this._url}/channels`, this.toPayload(body), { headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            return res;
                        }, catchError(err => {
                            if (err.status === 401) { // Unauthorized
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    PUTChannel(channelId:string, body:Channels){
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.put(`${this._url}/channels/${channelId}`, this.toUpdatePayload(body), { headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            return res;
                        }, catchError(err => {
                            if (err.status === 401) { // Unauthorized
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    private buildQueryString(paging: Paging): string {
        const params = new URLSearchParams({
            offset: String((paging.pageIndex - 1) * paging.pageSize),
            limit: String(paging.pageSize)
        });
        const sort = paging.sort.find((item) => item.value !== null);
        if (sort?.key) {
            params.set('sortBy', sort.key);
        }
        if (sort?.value === 'ascend') {
            params.set('sortOrder', 'asc');
        } else if (sort?.value === 'descend') {
            params.set('sortOrder', 'desc');
        }
        return `?${params.toString()}`;
    }

    private toPayload(body: Channels) {
        return {
            frequency: body.frequency,
            channel: (body.channel ?? []).map((item) => ({
                channelN: Number(item.channelN) || 0
            }))
        };
    }

    private toUpdatePayload(body: Channels) {
        return {
            channel: (body.channel ?? []).map((item) => ({
                channelN: Number(item.channelN) || 0
            }))
        };
    }
}
